import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "./api-client";
import type { UsageScreen } from "../types/api-types";

/**
 * "This person is looking at this screen" — the client half of usage tracking
 * (the server half is App\Controller\Api\UsageController, UsageTracker and
 * the daily counts of App\Service\Usage\UsageStats).
 *
 * One ping per screen displayed, never throttled here: the daily counts are
 * made of these, and the server already throttles the "last activity" write.
 * A person coming back to a tab they left in the background for a while counts
 * as looking at the screen again.
 *
 * Fire-and-forget by design: nothing in the UI waits for, or reports, a ping.
 */
const RETURN_AFTER_MS = 60_000;
let lastSentAt = 0;
let currentScreen: UsageScreen | null = null;

function send(screen: UsageScreen): void {
  lastSentAt = Date.now();
  void apiClient.sendUsage(screen);
}

/** The screen a route shows: the same closed set as App\Enum\UsageScreen. */
export function screenForPath(pathname: string): UsageScreen {
  const exact: Record<string, UsageScreen> = {
    "/coach": "coach_classes",
    "/coach/classes": "coach_classes",
    "/coach/presences": "coach_attendance",
    "/coach/tableau-de-bord/presences": "coach_attendance_stats",
    "/coach/tableau-de-bord": "coach_dashboard",
    "/coach/sessions": "coach_sessions",
    "/coach/calendar": "coach_calendar",
    "/coach/create-session": "coach_create_session",
    "/student": "student_home",
    "/student/programme": "student_program",
    "/student/presences": "student_attendance",
    "/student/flashcards": "student_flashcards",
    "/student/sessions": "student_sessions",
    "/admin": "admin_home",
    "/admin/users": "admin_users",
    "/admin/students": "admin_students",
    "/admin/audit-log": "admin_audit_log",
    "/admin/planning": "admin_planning",
    // The admin runs the same roll call as the coach; it counts as that screen.
    "/admin/presences": "coach_attendance",
    "/admin/classes": "admin_classes",
    "/admin/lieux": "admin_classes",
    "/admin/programs": "admin_programs",
    "/admin/club": "admin_club",
    "/techniques": "techniques",
  };
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  if (exact[path]) return exact[path];
  if (path.startsWith("/super-admin")) return "superadmin";
  if (path.startsWith("/coach/classes/")) return "coach_classes";
  if (path.startsWith("/coach/students/")) return "coach_student";
  if (/^\/admin\/programs\/[^/]+\/items\/[^/]+/.test(path)) return "admin_program_item";
  if (path.startsWith("/admin/programs/")) return "admin_program";
  if (path.startsWith("/technique/")) return "technique";
  if (path.startsWith("/session/")) return "session";

  return "other";
}

/**
 * Mounted once per signed-in layout: reports the screen every time the route
 * changes, and again when a tab that was left open comes back to the front.
 */
export function useUsageTracking(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    currentScreen = screenForPath(pathname);
    send(currentScreen);
  }, [pathname]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || currentScreen === null) return;
      if (Date.now() - lastSentAt >= RETURN_AFTER_MS) send(currentScreen);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
}
